import { Loader2 } from 'lucide-react';

import { cn } from '@/shared/lib/cn';

interface GoalsQualityIndicatorProps {
  /** 1..3 quality score; null/0 when not yet evaluated. */
  score?: number | null;
  /** Whether the project has any goals text at all. */
  hasGoals: boolean;
  /** True while a fresh evaluation is in flight. */
  pending?: boolean;
  className?: string;
  /** Visual size. */
  size?: 'sm' | 'md';
}

const BAR_HEIGHTS = ['h-1.5', 'h-2.5', 'h-3.5'] as const;
const BAR_HEIGHTS_SM = ['h-1', 'h-2', 'h-3'] as const;

/**
 * GSM-signal-style goals quality indicator, shared shape across all 3 frontends.
 *
 *  - No goals → a single amber dot (the brief is empty; nothing to evaluate).
 *  - Pending → a small spinner while EvaluateGoalsJob runs, so the row reads as
 *    "checking…" instead of a settled quality score.
 *  - Score 1/2/3 → that many ascending bars lit teal, the rest muted.
 *
 * Purely presentational — the score + pending state come straight from the
 * project resource (goals_quality_score / is_goals_evaluation_pending).
 */
export function GoalsQualityIndicator({
  score,
  hasGoals,
  pending = false,
  className,
  size = 'md',
}: GoalsQualityIndicatorProps) {
  // Empty brief: a single amber "needs goals" dot, never bars.
  if (!hasGoals) {
    return (
      <span
        className={cn(
          'inline-block rounded-full bg-warning',
          size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5',
          className
        )}
        aria-hidden
      />
    );
  }

  // Evaluation in flight — show processing, not a settled quality score.
  if (pending) {
    return (
      <Loader2
        className={cn(
          'animate-spin text-muted-foreground',
          size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5',
          className
        )}
        aria-hidden
      />
    );
  }

  const heights = size === 'sm' ? BAR_HEIGHTS_SM : BAR_HEIGHTS;
  const lit = Math.max(0, Math.min(3, score ?? 0));

  return (
    <span
      className={cn('inline-flex items-end gap-0.5', className)}
      aria-hidden
    >
      {heights.map((h, i) => (
        <span
          key={i}
          className={cn(
            'w-1 rounded-sm transition-colors',
            h,
            i < lit ? 'bg-primary' : 'bg-muted-foreground/25'
          )}
        />
      ))}
    </span>
  );
}
